import React from 'react';
import { useState } from 'react';
import { Camera, Award, Music, Heart, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useScrollAnimation } from '../hooks/useScrollAnimation';

const GalleryPage = () => {
  const scrollRef1 = useScrollAnimation();
  const scrollRef2 = useScrollAnimation();
  const [selectedImage, setSelectedImage] = useState<number | null>(null);

  const galleryImages = [
    {
      src: "/WhatsApp_Image_restauration.jpeg",
      alt: "Jean-Claude Buchta travaillant sur un piano à queue dans son atelier",
      category: "Atelier"
    },
    {
      src: "/image2.jpg",
      alt: "Jean-Claude Buchta travaillant sur un piano",
      category: "Artisan"
    },
    {
      src: "/image3.jpg",
      alt: "Showroom avec pianos Kawai et collection complète",
      category: "Showroom"
    },
    {
      src: "/image4.jpg",
      alt: "Piano à queue dans une salle de concert",
      category: "Concert"
    },
    {
      src: "/image6.jpg",
      alt: "Piano à queue dans un espace de concert moderne"
    },
    {
      src: "/imageskawai.jpeg",
      alt: "Showroom avec collection de pianos et banquettes rouges"
    },
    {
      src: "/image8.jpg",
      alt: "Jean-Claude Buchta dans son atelier de restauration"
    },
    {
      src: "/image9.jpg",
      alt: "Espace showroom avec pianos droits et tapis persan"
    },
    {
      src: "/image5.jpg",
      alt: "Jean-Claude Buchta avec piano à queue dans son magasin",
      category: "Expertise"
    },
    {
      src: "/expertise_buchta.jpg",
      alt: "Jean-Claude Buchta dans son atelier travaillant sur un piano",
      category: "Atelier"
    },
    {
      src: "/jeanclaude.jpeg",
      alt: "Jean-Claude Buchta accordant un piano à queue"
    },
    {
      src: "/demontage.jpeg",
      alt: "Façade du magasin Piano Musique Buchta"
    },
    {
      src: "/magasin.jpeg",
      alt: "Mécanisme de piano et pièces de restauration"
    },
    {
      src: "/expertise.jpeg",
      alt: "Jean-Claude Buchta au travail dans son atelier"
    },
    {
      src: "/jeanclaude2.jpeg",
      alt: "travail en atelier"
    }
  ];

  const openLightbox = (index: number) => {
    setSelectedImage(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    if (selectedImage !== null) {
      setSelectedImage((selectedImage + 1) % galleryImages.length);
    }
  };

  const prevImage = () => {
    if (selectedImage !== null) {
      setSelectedImage(selectedImage === 0 ? galleryImages.length - 1 : selectedImage - 1);
    }
  };

  // Gestion des touches clavier
  React.useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (selectedImage !== null) {
        if (e.key === 'Escape') closeLightbox();
        if (e.key === 'ArrowRight') nextImage();
        if (e.key === 'ArrowLeft') prevImage();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [selectedImage]);

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black"></div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div 
            ref={scrollRef1 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal text-center mb-16"
          >
            <h1 className="text-5xl md:text-7xl font-light heading-modern mb-6">
              Notre <span className="heading-accent">Galerie</span>
            </h1>
            <div className="w-32 h-px bg-gradient-to-r from-transparent via-yellow-400 to-transparent mx-auto mb-8"></div>
            <p className="text-xl text-white/70 font-light max-w-4xl mx-auto leading-relaxed">
              Découvrez notre univers à travers ces images : notre showroom, nos instruments d'exception, 
              et l'artisanat passionné qui anime notre quotidien.
            </p>
          </div>
        </div>
      </section>

      {/* Galerie d'images */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div 
            ref={scrollRef2 as React.RefObject<HTMLDivElement>}
            className="scroll-reveal"
          >
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {galleryImages.map((image, index) => (
                <div 
                  key={index} 
                  className="glass-card interactive-card rounded-2xl overflow-hidden light-effect cursor-pointer"
                  onClick={() => openLightbox(index)}
                >
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={image.src}
                      alt={image.alt}
                      className="w-full h-full object-cover transition-transform duration-700 hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors duration-300 flex items-center justify-center">
                      <div className="opacity-0 hover:opacity-100 transition-opacity duration-300">
                        <Camera className="w-8 h-8 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Section artisan */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="glass-card rounded-3xl p-12 max-w-4xl mx-auto text-center">
            <Heart className="h-16 w-16 text-yellow-400 mx-auto mb-6" />
            <h3 className="text-3xl font-light text-white mb-6">
              L'Art au Service de la <span className="heading-accent">Musique</span>
            </h3>
            <p className="text-white/70 font-light text-lg mb-8 leading-relaxed">
              Chaque image raconte une histoire : celle de la passion, du savoir-faire et de l'excellence 
              qui caractérisent notre approche de la musique depuis des décennies.
            </p>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex flex-col items-center">
                <Camera className="h-8 w-8 text-yellow-400 mb-3" />
                <h4 className="text-lg font-semibold text-white mb-2">Notre Showroom</h4>
                <p className="text-white/60 font-light text-sm">Un espace dédié à la découverte</p>
              </div>
              <div className="flex flex-col items-center">
                <Award className="h-8 w-8 text-yellow-400 mb-3" />
                <h4 className="text-lg font-semibold text-white mb-2">Instruments d'Exception</h4>
                <p className="text-white/60 font-light text-sm">Une sélection rigoureuse</p>
              </div>
              <div className="flex flex-col items-center">
                <Music className="h-8 w-8 text-yellow-400 mb-3" />
                <h4 className="text-lg font-semibold text-white mb-2">Savoir-Faire Artisanal</h4>
                <p className="text-white/60 font-light text-sm">La tradition au service de l'excellence</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedImage !== null && (
        <div className="fixed inset-0 bg-black/90 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          {/* Bouton fermer */}
          <button 
            onClick={closeLightbox}
            className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors z-10"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Bouton précédent */}
          <button 
            onClick={prevImage}
            className="absolute left-6 top-1/2 transform -translate-y-1/2 text-white/80 hover:text-white transition-colors z-10"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>

          {/* Bouton suivant */}
          <button 
            onClick={nextImage}
            className="absolute right-6 top-1/2 transform -translate-y-1/2 text-white/80 hover:text-white transition-colors z-10"
          >
            <ChevronRight className="w-8 h-8" />
          </button>

          {/* Image principale */}
          <div className="max-w-7xl max-h-full w-full h-full flex items-center justify-center">
            <img
              src={galleryImages[selectedImage].src}
              alt={galleryImages[selectedImage].alt}
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>

          {/* Indicateur de position */}
          <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 text-white/80 text-sm">
            {selectedImage + 1} / {galleryImages.length}
          </div>

          {/* Miniatures */}
          <div className="absolute bottom-20 left-1/2 transform -translate-x-1/2 flex space-x-2 max-w-full overflow-x-auto px-4">
            {galleryImages.map((image, index) => (
              <button
                key={index}
                onClick={() => setSelectedImage(index)}
                className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                  index === selectedImage ? 'border-yellow-400' : 'border-white/30 hover:border-white/60'
                }`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryPage;